/**
 * Helpers para validação de campos customizados (IDs da API como number|string,
 * valores currency como objeto ou JSON string, validation_rules como string ou objeto).
 */

export function findWorkflowFieldById<T extends { id: number | string }>(
  fields: T[],
  fieldId: number | string | null | undefined
): T | undefined {
  if (fieldId === undefined || fieldId === null || fieldId === '') {
    return undefined;
  }
  const n = Number(fieldId);
  if (!Number.isFinite(n)) {
    return undefined;
  }
  return fields.find((f) => Number(f.id) === n);
}

export function parseValidationRulesJson(raw: unknown): { rules: any[] } | null {
  if (raw == null || raw === '') {
    return null;
  }
  if (typeof raw === 'object' && raw !== null && Array.isArray((raw as { rules?: unknown }).rules)) {
    return raw as { rules: any[] };
  }
  if (typeof raw !== 'string') {
    return null;
  }
  const t = raw.trim();
  if (!t) {
    return null;
  }
  try {
    const parsed = JSON.parse(t) as { rules?: unknown };
    if (parsed && Array.isArray(parsed.rules)) {
      return parsed as { rules: any[] };
    }
  } catch {
    return null;
  }
  return null;
}

/** Valor do campo usado como fonte da validação (ex.: moeda) ainda não informado. */
export function isValidationSourceValueEmpty(raw: unknown): boolean {
  if (raw === undefined || raw === null || raw === '') {
    return true;
  }
  if (typeof raw === 'object' && raw !== null && 'value' in raw) {
    const inner = (raw as { value: unknown }).value;
    return inner === undefined || inner === null || String(inner).trim() === '';
  }
  return false;
}

/**
 * Extrai número para faixas de valor / comparações; cobre currency JSON, objeto em estado e decimais.
 */
export function parseCustomFieldNumericValue(rawValue: unknown, fieldType?: string): number {
  if (rawValue === undefined || rawValue === null || rawValue === '') {
    return NaN;
  }

  if (typeof rawValue === 'object' && rawValue !== null && !Array.isArray(rawValue) && 'value' in rawValue) {
    const inner = (rawValue as { value: unknown }).value;
    if (inner === undefined || inner === null || inner === '') {
      return NaN;
    }
    const num = parseFloat(String(inner).replace(/[^\d.-]/g, ''));
    return Number.isFinite(num) ? num : NaN;
  }

  if (fieldType === 'currency') {
    try {
      const parsed =
        typeof rawValue === 'string' ? JSON.parse(rawValue) : (rawValue as { value?: unknown });
      if (parsed && typeof parsed === 'object' && parsed.value !== undefined) {
        const num = parseFloat(String(parsed.value).replace(/[^\d.-]/g, ''));
        return Number.isFinite(num) ? num : NaN;
      }
    } catch {
      /* continua */
    }
  }

  if (typeof rawValue === 'string' && rawValue.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(rawValue) as { value?: unknown };
      if (parsed && typeof parsed === 'object' && parsed.value !== undefined) {
        const num = parseFloat(String(parsed.value).replace(/[^\d.-]/g, ''));
        if (Number.isFinite(num)) {
          return num;
        }
      }
    } catch {
      /* ignore */
    }
  }

  const rawStr = String(rawValue);
  const parsed = parseFloat(rawStr.replace(/[^\d.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : NaN;
}
